import React, { useState, useEffect, useCallback, useMemo, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "sonner";
import { applyCategories } from "./lib/api";

// Context
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios
axios.defaults.withCredentials = true;

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [athletes, setAthletes] = useState([]);
  const [activeAthlete, setActiveAthlete] = useState(null);
  const [categories, setCategories] = useState([]);

  const loadCategories = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/categories`);
      const list = res.data || [];
      applyCategories(list);
      setCategories(list);
    } catch (error) {
      if (process.env.NODE_ENV === "development") console.error("Failed to load categories", error);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
      // Categories require auth — load after we have a user
      await loadCategories();
    } catch (error) {
      setUser(null);
      setAthletes([]);
      setActiveAthlete(null);
    } finally {
      setLoading(false);
    }
  }, [loadCategories]);

  const loadAthletes = useCallback(async () => {
    try {
      const res = await axios.post(`${API}/athletes/ensure`);
      const list = res.data.athletes || [];
      setAthletes(list);
      // Restore last selected or pick first
      const savedId = localStorage.getItem("activeAthleteId");
      const found = list.find(a => a.athlete_id === savedId);
      setActiveAthlete(found || list[0] || null);
    } catch (error) {
      if (process.env.NODE_ENV === "development") console.error("Failed to load athletes", error);
    }
  }, []);

  // Load athletes after user is set
  useEffect(() => {
    if (user) {
      loadAthletes();
    }
  }, [user, loadAthletes]);

  const switchAthlete = useCallback((athlete) => {
    setActiveAthlete(athlete);
    localStorage.setItem("activeAthleteId", athlete.athlete_id);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback((userData) => {
    setUser(userData);
    loadCategories();
  }, [loadCategories]);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      if (process.env.NODE_ENV === "development") console.error("Logout error:", error);
    }
    setUser(null);
    setAthletes([]);
    setActiveAthlete(null);
  }, []);

  const contextValue = useMemo(
    () => ({ user, loading, login, logout, checkAuth, athletes, activeAthlete, switchAthlete, loadAthletes, categories, loadCategories }),
    [user, loading, login, logout, checkAuth, athletes, activeAthlete, switchAthlete, loadAthletes, categories, loadCategories]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Lazy load pages
const Landing = React.lazy(() => import("./pages/Landing"));
const Login = React.lazy(() => import("./pages/Login"));
const ForgotPassword = React.lazy(() => import("./pages/ForgotPassword"));
const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const Profile = React.lazy(() => import("./pages/Profile"));
const AdminDashboard = React.lazy(() => import("./pages/AdminDashboard"));
const AdminTimeSlots = React.lazy(() => import("./pages/AdminTimeSlots"));
const AdminUsers = React.lazy(() => import("./pages/AdminUsers"));
const AdminMessages = React.lazy(() => import("./pages/AdminMessages"));
const AdminReports = React.lazy(() => import("./pages/AdminReports"));
const AdminSlotRequests = React.lazy(() => import("./pages/AdminSlotRequests"));
const AdminCategories = React.lazy(() => import("./pages/AdminCategories"));
const AdminBackup = React.lazy(() => import("./pages/AdminBackup"));
const UserHelp = React.lazy(() => import("./pages/UserHelp"));
const AdminHelp = React.lazy(() => import("./pages/AdminHelp"));
const ForcePasswordReset = React.lazy(() => import("./pages/ForcePasswordReset"));

// Protected Route
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading, checkAuth } = useAuth();
  const location = useLocation();
  const navState = useMemo(() => ({ from: location }), [location]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={navState} replace />;
  }

  if (user.force_password_reset && location.pathname !== "/force-reset-password") {
    return <Navigate to="/force-reset-password" replace />;
  }

  if (adminOnly && !user.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Force Password Reset Wrapper
const ForcePasswordResetWrapper = () => {
  const { user, checkAuth } = useAuth();
  return <ForcePasswordReset user={user} checkAuth={checkAuth} />;
};

// App Router
const AppRouter = () => {
  return (
    <React.Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      }
    >
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route
          path="/force-reset-password"
          element={
            <ProtectedRoute>
              <ForcePasswordResetWrapper />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute adminOnly>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/timeslots"
          element={
            <ProtectedRoute adminOnly>
              <AdminTimeSlots />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute adminOnly>
              <AdminUsers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/messages"
          element={
            <ProtectedRoute adminOnly>
              <AdminMessages />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/reports"
          element={
            <ProtectedRoute adminOnly>
              <AdminReports />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/slot-requests"
          element={
            <ProtectedRoute adminOnly>
              <AdminSlotRequests />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/categories"
          element={
            <ProtectedRoute adminOnly>
              <AdminCategories />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/backup"
          element={
            <ProtectedRoute adminOnly>
              <AdminBackup />
            </ProtectedRoute>
          }
        />
        <Route
          path="/help"
          element={
            <ProtectedRoute>
              <UserHelp />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/help"
          element={
            <ProtectedRoute adminOnly>
              <AdminHelp />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </React.Suspense>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-center" richColors />
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
